// Unread state at launch. Slack only gives its own app per-conversation unread counts
// (client.counts); third-party apps get each conversation's read marker (last_read)
// and its messages, so we work it out per conversation, most active first, at a pace
// that stays inside Slack's rate limits. Pure parts are tested in unreads.test.ts.

import type { Conversation, SlackMessage } from '@assembly/slack'

/** Message subtypes that count as unread, like Slack (joins/leaves/topic changes don't). */
const COUNTED_SUBTYPES = new Set([undefined, 'thread_broadcast', 'bot_message', 'me_message', 'file_share'])

export interface UnreadResult {
  unread: number
  mentions: number
  /** True when there were more unread messages than we fetched. */
  more: boolean
  /** Newest message ts, for "mark as read". */
  latest?: string
}

const tsNum = (ts: string | undefined) => (ts ? Number(ts) : 0)

/**
 * Counts unread messages after `lastRead`. Mentions are @you, @here/@channel/@everyone,
 * or any message in a DM / group DM (they all badge in Slack).
 */
export function countUnread(messages: SlackMessage[], lastRead: string | undefined, me: string, direct: boolean, more = false): UnreadResult {
  const after = tsNum(lastRead)
  let unread = 0
  let mentions = 0
  let latest: string | undefined
  for (const m of messages) {
    if (!latest || tsNum(m.ts) > tsNum(latest)) latest = m.ts
    if (tsNum(m.ts) <= after) continue
    if (m.user === me || !COUNTED_SUBTYPES.has(m.subtype)) continue
    if (m.thread_ts && m.thread_ts !== m.ts && m.subtype !== 'thread_broadcast') continue
    unread++
    if (direct || m.text?.includes(`<@${me}>`) || /<!(here|channel|everyone)(\|[^>]*)?>/.test(m.text ?? '')) mentions++
  }
  return { unread, mentions, more: more && unread > 0, latest }
}

/**
 * Order to check conversations in: the open one, then DMs and channels by most recent
 * activity, so what you're likely to look at gets its badge first. Archived and
 * deactivated-user DMs are skipped.
 */
export function scanOrder(convs: Conversation[], opts: { current?: string | null; skip?: (c: Conversation) => boolean } = {}): Conversation[] {
  return convs
    .filter((c) => !c.is_archived && !c.is_user_deleted && !opts.skip?.(c))
    .sort((a, b) => Number(b.id === opts.current) - Number(a.id === opts.current) || (b.updated ?? 0) - (a.updated ?? 0) || Number(!!b.is_im) - Number(!!a.is_im))
}

/** Spaces out calls to one Slack method (Tier 3 ≈ 50/minute) across all callers. */
export class Pacer {
  private next = 0
  constructor(
    private intervalMs: number,
    private now = () => Date.now(),
    private sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
  ) {}

  async wait() {
    const t = this.now()
    const at = Math.max(t, this.next)
    this.next = at + this.intervalMs
    if (at > t) await this.sleep(at - t)
  }
}
